/*    
<summary>
   This component is Managing Integrators & performing CRUD operations on the them.
   CRUD (Create, Read, Update, Delete)
   Developer: Urvashi, Created Date:25-April-2024
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/
import { observer } from "mobx-react";
import React, { SyntheticEvent, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FormattedMessage } from "react-intl";
import { useHistory } from "react-router-dom";
import SlidingPanel from "react-sliding-side-panel";
import "react-sliding-side-panel/lib/index.css";
import noMessageIcon from "../../assets/Images/svg/018-empty.svg";
import { ModuleType } from "../../constants/enums/module-type-enum";
import { parkingLotStatus } from "../../constants/enums/parking-lot-status-enum";
import userSettingEnum from "../../constants/enums/user-setting-enum";
import RoutesConstants from "../../constants/routes.constant";
import { useStore } from "../../contexts/store-provider";
import {
  isOperationPermittedParkingRoleBase,
  isOperationPermittedRoleBase,
  showIconsRoleBase,
} from "../../helpers/action-permission-helper";
import {
  getIsTenantAccessFromLocalStorage,
  getTenantAccessFromLocalStorage,
} from "../../helpers/localstorage-helper";
import IPageInfo, {
  IActionFormatter,
  IPageLimitInfo,
} from "../../models/ICommon";
import LoaderButton from "../../shared-components/button/loader-button";
import FormLoader from "../../shared-components/FormLoader/FormLoader";
import Grid from "../../shared-components/Grid/Grid";
import {
  CustomActionFormatter,
  CustomIntegratorActionFormatter,
} from "../../shared-components/Grid/GridFormatter";
import Pagination from "../../shared-components/Grid/Pagination";
import ConfirmPopup from "../../shared-components/popup/confirm/confirm-popup";
import ConfirmPopupHOC from "../../shared-components/popup/confirm/confirm-popup-hoc";
import DeletePopup from "../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../shared-components/popup/delete/delete-popup-hoc";
import { formatMessage } from "../../translations/format-message";
import AddEditIntegratorImageStatus from "../integrator-status-image/components/add-edit-integrator-image-status";
import AddIntegratorStatusImagesPopupHOC from "../integrator-status-image/hoc/AddIntegratorStatusImagesPopupHOC";
import AddEditIntegratorThreshold from "../integrator-threshold/components/add-edit-integrator-threshold";
import ThresholdIntegratorLinkbaseOutPopupHOC from "../integrator-threshold/hoc/ThresholdIntegratorLinkbaseOutPopupHOC";
import AddEditIntegrator from "./components/add-edit-integrator";
import AddIntegratorPopupHOC from "./hoc/AddIntegratorPopUpHOC";
import IntegratorDetails from "./integrator-details";
import "./integrator.css";

const Integrator = (props: any) => {
  const history = useHistory();
  const { preferencesStore, authStore, integratorStore } = useStore();
  const {
    userSetting,
    setUserSetting,
    language,
    islanguageChanged,
    updateLanguageChangedFlag,
    setManageLinkbaseOutAccess,
    setManageIntegratorLinkbaseOutAccess,
    getTenantAccess,
  } = preferencesStore;
  const {
    allIntegratorList,
    addUpdateIntegratorState,
    integratorList,
    GetIntegratorsListService,
    DeleteIntegratorService,
    resetAddUpdateIntegratorState,
    resetIntegratorDetail,
    resetDeleteIntegratorState,
    deleteIntegratorState,
    inProgress,
    updateIntegratorStatusState,
    resetUpdateIntegratoStatusState,
    UpdateIntegratorStatusService,
  } = integratorStore;
  let lang: string;
  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const [openPanel, setOpenPanel] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>({});
  const [selectedId, setSelectedId] = useState<number>(0);
  const [currentStatus, setCurrentStatus] = useState<number>(0);
  const [selectedIntegratorId, setSelectedIntegratorId] = useState<string>("");
  const integratorData: Array<any> = allIntegratorList;
  const pageLimitOptions: Array<number> = [10, 50, 100];
  const [pageLimitInfo, setPageLimit] = useState<IPageLimitInfo>({
    pageLimit: userSetting.pageLimitConfig,
    isPageLimitChange: false,
  });

  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });

  const onPageChanged = (pageNumber: number) => {
    setPageInfo({ ...pageInfo, currentPage: pageNumber, isPagerChange: true });
  };

  const setIsRefreshPage = () => {
    setPageInfo({ ...pageInfo, isRefreshPage: false });
  };

  const onPageLimitChanged = (event: any) => {
    let pages: number;
    pages = props.data && Math.ceil(props.data.length / event.target.value);
    setPageInfo({
      ...pageInfo,
      currentPage: 1,
      totalPages: pages,
      isPagerChange: false,
    });
    setPageLimit({
      pageLimit: parseInt(event.target.value),
      isPageLimitChange: true,
    });
    setUserSetting(
      userSettingEnum.UserLimitConfig,
      parseInt(event.target.value)
    );
  };

  const callGetIntegratorListService = () => {
    GetIntegratorsListService(
      pageInfo.currentPage,
      pageLimitInfo.pageLimit,
      getTenantAccessFromLocalStorage()
    );
  };

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "language" of the useEffect changed.
   */
  useEffect(() => {
    if (islanguageChanged) {
      callGetIntegratorListService();
      updateLanguageChangedFlag();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [islanguageChanged]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "GroupList" of the useEffect changed.
   */
  useEffect(() => {
    setPageInfo({
      ...pageInfo,
      totalPages: integratorList?.PagingDetails?.TotalPages,
      isPagerChange: false,
    });
    setDisablePagination(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integratorList]);

  useEffect(() => {
    if (pageInfo.isPagerChange) {
      callGetIntegratorListService();
    }
  }, [pageInfo.currentPage]);

  useEffect(() => {
    if (updateIntegratorStatusState.success) {
      toast.success(formatMessage("updated_success"));
      props.confirmPopupToggleHandler();
      callGetIntegratorListService();
      resetUpdateIntegratoStatusState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateIntegratorStatusState.success]);

  const openAddIntegratorHandler = (
    row: { Id: number },
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    closePanelhandler();
    resetIntegratorDetail();
    event.stopPropagation();
    setSelectedId(row.Id);
    props.addIntegratorToggleHandler();
  };

  const openAccessLinkBaseOut = (
    row: any,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    var integratorName: string = row.IntegratorName;
    history.push({
      pathname: RoutesConstants.IntegratorLinkbaseOut,
      state: integratorName,
    });
    event.stopPropagation();
    setManageIntegratorLinkbaseOutAccess({
      integratorId: row.Id,
      integratorName: row.IntegratorName,
      isIntegratorLinkBaseOutAccess: true,
    });
    setSelectedId(row.PlId);
  };

  const openAddImageStatusHandler = (
    row: { Id: number },
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    closePanelhandler();
    resetIntegratorDetail();
    setSelectedId(row.Id);
    event.stopPropagation();
    props.addIntegratorStatusImagesToggleHandler();
  };

  const openAddThresholdHandler = (
    row: any,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    closePanelhandler();
    resetIntegratorDetail();
    setSelectedId(row.Id);
    setSelectedIntegratorId(row.IntegratorId);
    event.stopPropagation();
    props.addIntegratorThresholdLinkbaseOutToggleHandler();
  };

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateBranchState.success" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateIntegratorState.success) {
      callGetIntegratorListService();
      resetAddUpdateIntegratorState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUpdateIntegratorState.success]);

  useEffect(() => {
    if (deleteIntegratorState.success) {
      toast.success(formatMessage("deleted_success"));
      resetDeleteIntegratorState();
      props.deletePopupToggleHandler();

      if (integratorData?.length == 1) {
        if (pageInfo.currentPage > 1)
          setPageInfo({
            ...pageInfo,
            currentPage: 1,
            isRefreshPage: true,
            isPagerChange: true,
          });
        else {
          callGetIntegratorListService();
        }
      } else {
        callGetIntegratorListService();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteIntegratorState.success]);

  useEffect(() => {
    if (pageLimitInfo.isPageLimitChange) {
      callGetIntegratorListService();
      setPageLimit({ ...pageLimitInfo, isPageLimitChange: false });
    }
  }, [pageLimitInfo.pageLimit]);

  /**
   * This function shows the delete branch confirmation popup component and sets the seleted branch Id
   */
  const deleteClickHandler = (
    row: { Id: number },
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setSelectedId(row.Id);
    event.stopPropagation();
    closePanelhandler();
    resetIntegratorDetail();
    props.deletePopupToggleHandler();
  };

  const deleteHandler = () => {
    DeleteIntegratorService(selectedId, getTenantAccessFromLocalStorage());
  };

  const changeParkingLotStatusHandler = (
    row: any,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    closePanelhandler();
    setSelectedId(row.Id);
    setCurrentStatus(row.CurrentStatus);
    event.stopPropagation();
    props.confirmPopupToggleHandler();
  };

  const integratorChangeStatusHandler = () => {
    let obj = {
      TenantId: getTenantAccessFromLocalStorage(),
      Id: selectedId,
      CurrentStatus:
        currentStatus == parkingLotStatus.Available ||
        currentStatus == parkingLotStatus.Crowded ||
        currentStatus == parkingLotStatus.Full
          ? parkingLotStatus.Closed
          : parkingLotStatus.Available,
    };
    UpdateIntegratorStatusService(obj);
    // DeleteParkingLotService(selectedId, getTenantAccessFromLocalStorage());
  };

  useEffect(() => {
    if (language !== lang) {
      callGetIntegratorListService();
    }
  }, [language]);

  function rowClassFormat(row: any, rowIdx: number) {
    let className: string = "";
    if (selectedRow?.Id && row.Id === selectedRow.Id) {
      className = "bg-row SelectedRow";
    }
    return className;
  }

  const rowEvents = (e: SyntheticEvent, row: any, rowIndex: number) => {
    setSelectedRow(row);
    setOpenPanel(true);
  };

  const closePanelhandler = () => {
    setSelectedRow({});
    setOpenPanel(false);
  };

  const actionIcons: IActionFormatter[] = showIconsRoleBase(
    [
      {
        dataField: "isLinkbaseOut",
        handler: openAccessLinkBaseOut,
        icon: (
          <svg
            id="Layer_2"
            data-name="Layer 2"
            fill="#0091ff"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24.18 25.26"
          >
            <g id="_レイヤー_3" data-name="レイヤー 3">
              <g>
                <rect x="9.25" y="13.93" width="5.68" height="6.29" />
                <polygon points="12.09 25.26 8.99 22.36 5.88 19.45 12.09 19.45 18.3 19.45 15.19 22.36 12.09 25.26" />
                <g>
                  <path d="M6.86,5.73c1.61,0,2.92,1.19,2.92,2.94s-1.32,2.94-2.92,2.94-2.92-1.17-2.92-2.94,1.32-2.94,2.92-2.94Zm0,4.79c1.07,0,1.73-.91,1.73-1.85s-.66-1.85-1.73-1.85-1.73,.91-1.73,1.85,.66,1.85,1.73,1.85Z" />
                  <path d="M15.14,5.84v3.27c0,.57,0,2.5-2.43,2.5s-2.43-1.94-2.43-2.49v-3.28h1.19v3.22c0,.51,0,1.46,1.24,1.46s1.24-.94,1.24-1.46v-3.22h1.19Z" />
                  <path d="M18.38,11.5h-1.19V6.93h-1.71v-1.09h4.61v1.09h-1.71v4.57Z" />
                </g>
                <path d="M20.89,0H3.29C1.48,0,0,1.48,0,3.29V20.89c0,1.81,1.48,3.29,3.29,3.29h2.16v-2.54H3.29c-.42,0-.75-.34-.75-.76V3.29c0-.42,.34-.75,.75-.75H20.89c.42,0,.75,.34,.75,.75V20.89c0,.42-.34,.76-.75,.76h-2.16v2.54h2.16c1.81,0,3.29-1.48,3.29-3.29V3.29c0-1.81-1.48-3.29-3.29-3.29Z" />
              </g>
            </g>
          </svg>
        ),
        isDisable: true,
        title: "linkbase_out",
        varient: "primary",
        action: "view",
        hidden: isOperationPermittedParkingRoleBase(
          ModuleType.ParkingManagementButtons,
          "Hide"
        ),
      },
      {
        dataField: "isThreshold",
        handler: openAddThresholdHandler,
        icon: (
          <svg
            id="Layer_2"
            width="30"
            height="30"
            data-name="Layer 2"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 25.52 25.81"
          >
            <g id="_レイヤー_3" data-name="レイヤー 3">
              <g>
                <rect
                  fill="#0091ff"
                  x="3.69"
                  y="11.24"
                  width="10.72"
                  height="7.25"
                />
                <rect
                  fill="#0091ff"
                  x="13.39"
                  y="2.93"
                  width="2.03"
                  height="22.89"
                />
                <path
                  fill="#0091ff"
                  d="M2.64,4.43l-.41,1.23H.92L2.98,0h1.49l2.06,5.66h-1.31l-.41-1.23H2.64Zm1.82-1.01l-.74-2.23-.75,2.23h1.49Z"
                />
                <path
                  fill="#0091ff"
                  d="M22.63,0c.42,0,.75,.07,.97,.16,.49,.2,.88,.66,.88,1.32,0,.7-.37,1.01-.58,1.19,.23,.15,.75,.49,.75,1.37,0,1.61-1.56,1.61-2.07,1.61h-1.99V0h2.05Zm-.86,1.09v1.15h.87c.54,0,.65-.4,.65-.58,0-.2-.1-.57-.71-.57h-.81Zm0,2.13v1.35h.87c.18,0,.82,0,.82-.67,0-.5-.37-.68-.75-.68h-.94Z"
                />
                <path
                  fill="#0091ff"
                  d="M23.35,22.4H2.18c-1.2,0-2.18-.98-2.18-2.18V9.5c0-1.2,.98-2.18,2.18-2.18H23.35c1.2,0,2.18,.98,2.18,2.18v10.72c0,1.2-.98,2.18-2.18,2.18Zm-.06-12.9l-21.11,.06,.06,10.66,21.05-.06V9.5Z"
                />
              </g>
            </g>
          </svg>
        ),
        isDisable: true,
        title: "threshold_management",
        varient: "primary",
        action: "allAccess",
        hidden: isOperationPermittedParkingRoleBase(
          ModuleType.ParkingManagementButtons,
          "Hide"
        ),
      },
      {
        dataField: "isImage",
        handler: openAddImageStatusHandler,
        icon: (
          <svg
            fill="#0091ff"
            id="Layer_2"
            data-name="Layer 2"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 25.34 24.09"
          >
            <g id="_レイヤー_3" data-name="レイヤー 3">
              <g>
                <g>
                  <path d="M7.49,7.03v2.61c-2.22,.48-3.88,2.45-3.88,4.81,0,.84,.22,1.66,.63,2.39l-1.87,1.87c-.87-1.25-1.34-2.72-1.34-4.26,0-3.78,2.81-6.91,6.45-7.42m1.04-1.11C3.82,5.91,0,9.73,0,14.44c0,2.25,.88,4.29,2.3,5.81l3.3-3.3c-.58-.68-.94-1.55-.94-2.51,0-2.14,1.74-3.88,3.88-3.88V5.91h0Z" />
                  <g>
                    <path d="M9.51,5.97v4.73c1.67,.43,2.9,1.94,2.9,3.74,0,2.14-1.74,3.88-3.88,3.88-.77,0-1.48-.25-2.08-.64l-3.33,3.33c1.47,1.22,3.35,1.96,5.41,1.96,4.71,0,8.53-3.82,8.53-8.53,0-4.38-3.3-7.99-7.56-8.47Z" />
                    <path d="M9.51,5.97v4.73c1.67,.43,2.9,1.94,2.9,3.74,0,2.14-1.74,3.88-3.88,3.88-.77,0-1.48-.25-2.08-.64l-3.33,3.33c1.47,1.22,3.35,1.96,5.41,1.96,4.71,0,8.53-3.82,8.53-8.53,0-4.38-3.3-7.99-7.56-8.47h0Z" />
                  </g>
                </g>
                <path d="M22.67,24.09h-6.36v-2.24h6.36c.23,0,.43-.19,.43-.42V7.13l-4.6-4.89h-5.75c-.23,0-.42,.19-.42,.43v1.72h-2.24v-1.72c0-1.47,1.2-2.67,2.66-2.67h6.72l5.86,6.24v15.19c0,1.47-1.2,2.66-2.67,2.66Z" />
                <path d="M24.22,7.76h-4.4c-1.37,0-2.48-1.11-2.48-2.48V1.12h2.24V5.27c0,.13,.11,.24,.24,.24h4.4v2.24Z" />
              </g>
            </g>
          </svg>
        ),
        isDisable: true,
        title: "integrator_image_status_title",
        varient: "primary",
        action: "allAccess",
        hidden: isOperationPermittedParkingRoleBase(
          ModuleType.Integrator,
          "Hide"
        ),
      },
      {
        dataField: "isEdit",
        handler: openAddIntegratorHandler,
        icon: (
          <svg
            id="Layer_2"
            fill="#0091ff"
            width="21"
            height="21"
            data-name="Layer 2"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 28.43 24.75"
          >
            <g id="_レイヤー_3" data-name="レイヤー 3">
              <g>
                <path d="M10.53,15.53l1.19,1.19,1.19,1.19-1.85,.58-1.61,.5,.5-1.61,.58-1.85m-.45-1.81l-1.05,3.37-1.05,3.37,3.37-1.05,3.37-1.05-2.32-2.32-2.32-2.32h0Z" />
                <polygon points="7.97 20.46 8.57 18.54 9.17 16.62 10.49 17.94 11.81 19.26 9.89 19.86 7.97 20.46" />
                <path d="M15.22,19.37l-6.15-6.15L22.28,0l6.15,6.15-13.21,13.21Zm-3.12-6.15l3.12,3.12L25.4,6.15l-3.12-3.12L12.1,13.21Z" />
                <path d="M23.57,24.75H4.47c-2.46,0-4.47-2-4.47-4.47v-6.38c0-2.46,2-4.47,4.47-4.47h2.36v2.24h-2.36c-1.23,0-2.23,1-2.23,2.23v6.38c0,1.23,1,2.23,2.23,2.23H23.57c1.23,0,2.23-1,2.23-2.23v-7.47h2.24v7.47c0,2.46-2,4.47-4.47,4.47Z" />
                <rect
                  x="13.01"
                  y="8.8"
                  width="11.25"
                  height="1.3"
                  transform="translate(-1.22 15.95) rotate(-45)"
                />
              </g>
            </g>
          </svg>
        ),
        isDisable: true,
        title: "edit",
        action: "edit",
        varient: "primary",
      },
      {
        dataField: "isDelete",
        handler: deleteClickHandler,
        icon: (
          <svg
            id="Layer_2"
            fill="#0091ff"
            width="18"
            height="18"
            data-name="Layer 2"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20.09 20.66"
          >
            <g id="_レイヤー_3" data-name="レイヤー 3">
              <g>
                <polygon points="12.12 1.99 12.12 0 10.05 0 7.97 0 7.97 1.99 0 1.99 0 4.07 10.05 4.07 20.09 4.07 20.09 1.99 12.12 1.99" />
                <path d="M14.29,17.76c-.02,.37-.32,.66-.69,.66H6.49c-.37,0-.67-.29-.69-.65l-.53-12.44-2.24,.09,.53,12.45c.08,1.56,1.37,2.78,2.93,2.78h7.11c1.56,0,2.85-1.22,2.93-2.79l.53-12.44-2.24-.09-.53,12.43Z" />
                <rect x="11.68" y="6.8" width="1.16" height="9.8" />
              </g>
            </g>
          </svg>
        ),
        isDisable: true,
        title: "delete",
        action: "delete",
        varient: "danger",
      },
    ],
    ModuleType.ParkingManagementList
  );

  const columns: any = [
    {
      dataField: "Id",
      text: formatMessage("label_id"),
      hidden: true,
    },
    {
      dataField: "IntegratorName",
      text: formatMessage("integrator_name"),
      title: (cell: string, row: any) => {
        return cell.length > 10 ? cell : undefined;
      },
    },
    {
      dataField: "IntegratorId",
      text: formatMessage("integrator_id"),
    },
    {
      dataField: "Comment",
      classes: "address-Tenant",
      text: formatMessage("comment"),
      title: (cell: string, row: any) => {
        return cell.length > 10 ? cell : undefined;
      },
    },
    {
      dataField: "CurrentStatus",
      classes: "btn-light-danger parking-status",
      text: formatMessage("parking_lot_status"),
      formatter: (cell: any, row: any) => (
        <span
          className={`badge badge-parking ${
            row.CurrentStatus == parkingLotStatus.Available
              ? "badge-light-success"
              : row.CurrentStatus == parkingLotStatus.Closed
              ? "badge-light-dark"
              : row.CurrentStatus == parkingLotStatus.Crowded
              ? "badge-light-secondary"
              : row.CurrentStatus == parkingLotStatus.Full
              ? "badge-light-danger"
              : ""
          }`}
        >
          {row.CurrentStatus != -1 && (
            <FormattedMessage id={parkingLotStatus[row.CurrentStatus]} />
          )}
        </span>
      ),
    },
    {
      dataField: "CurrentStatus",
      classes: "text-center open-close",
      text: formatMessage("open_close"),
      hidden: isOperationPermittedParkingRoleBase(
        ModuleType.Integrator,
        "Hide"
      ),
      formatter: CustomIntegratorActionFormatter([
        {
          dataField: "isIntegratorOpenClosed",
          handler: changeParkingLotStatusHandler,
          icon2: (
            <svg
              fill="rgb(134, 137, 144)"
              version="1.1"
              width="20"
              height="20"
              xmlns="http://www.w3.org/2000/svg"
              x="0px"
              y="0px"
              viewBox="0 0 405.272 405.272"
            >
              <g>
                <path
                  d="M393.401,124.425L179.603,338.208c-15.832,15.835-41.514,15.835-57.361,0L11.878,227.836
                c-15.838-15.835-15.838-41.52,0-57.358c15.841-15.841,41.521-15.841,57.355-0.006l81.698,81.699L336.037,67.064
                c15.841-15.841,41.523-15.829,57.358,0C409.23,82.902,409.23,108.578,393.401,124.425z"
                />
              </g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
            </svg>
          ),
          icon1: (
            <svg
              fill="rgb(239, 40, 65)"
              version="1.1"
              width="20"
              height="20"
              xmlns="http://www.w3.org/2000/svg"
              x="0px"
              y="0px"
              viewBox="0 0 405.272 405.272"
            >
              <g>
                <path
                  d="M393.401,124.425L179.603,338.208c-15.832,15.835-41.514,15.835-57.361,0L11.878,227.836
                c-15.838-15.835-15.838-41.52,0-57.358c15.841-15.841,41.521-15.841,57.355-0.006l81.698,81.699L336.037,67.064
                c15.841-15.841,41.523-15.829,57.358,0C409.23,82.902,409.23,108.578,393.401,124.425z"
                />
              </g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
              <g></g>
            </svg>
          ),
          isDisable: true,
          title1: "close_integrator",
          title2: "open_integrator",
          title: "image_status_title",
          varient: "primary",
          action: "allAccess",
          hidden: isOperationPermittedParkingRoleBase(
            ModuleType.Integrator,
            "Hide"
          ),
        },
      ]),
    },
    {
      dataField: "",
      formatter: CustomActionFormatter(actionIcons),
      text: formatMessage("actions"),
      classes: "last-column",
    },
  ];
  return (
    <React.Fragment>
      {props.showAddIntegratorPopup && (
        <AddEditIntegrator
          id={selectedId}
          modalClosed={props.addIntegratorToggleHandler}
        />
      )}

      {props.showAddIntegratorStatusImagesPopup && (
        <AddEditIntegratorImageStatus
          id={selectedId}
          modalClosed={props.addIntegratorStatusImagesToggleHandler}
        />
      )}

      {props.showAddIntegratorThresholdLinkbaseOutPopup && (
        <AddEditIntegratorThreshold
          id={selectedId}
          integratorID={selectedIntegratorId}
          modalClosed={props.addIntegratorThresholdLinkbaseOutToggleHandler}
        />
      )}

      {props.showDeletePopup && (
        <DeletePopup
          title="delete_integrator"
          modalSubmit={deleteHandler}
          modalClosed={props.deletePopupToggleHandler}
          message="message_delete_integrator_confirm"
          isLoading={deleteIntegratorState.inProgress}
        />
      )}

      {props.showConfirmPopup && (
        <ConfirmPopup
          title={
            currentStatus == parkingLotStatus.Available ||
            currentStatus == parkingLotStatus.Crowded ||
            currentStatus == parkingLotStatus.Full
              ? "close_integrator"
              : "open_integrator"
          }
          modalSubmit={integratorChangeStatusHandler}
          modalClosed={props.confirmPopupToggleHandler}
          message={
            currentStatus == parkingLotStatus.Available ||
            currentStatus == parkingLotStatus.Crowded ||
            currentStatus == parkingLotStatus.Full
              ? "close_integrator_message"
              : "open_integrator_message"
          }
          isLoading={updateIntegratorStatusState.inProgress}
        />
      )}

      {inProgress && <FormLoader loading={inProgress} />}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-xl-6 col-lg-6 col-md-8 col-12">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="integrator_management" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="Home" />
                </li>
                {getIsTenantAccessFromLocalStorage() == true && (
                  <li className="breadcrumb-item">
                    {getTenantAccess.tenantName}
                  </li>
                )}
                {/* <li className="breadcrumb-item active" aria-current="page">
                  <FormattedMessage id="BranchManagement" />
                </li> */}
              </ol>
            </div>
          </div>
          <div className="col-xl-6 col-lg-6 col-md-4 col-12">
            <ul className="add-list">
              {isOperationPermittedRoleBase(ModuleType.SoracomAuth, "Add") && (
                <li title={formatMessage("add_integrator")} className="ms-1">
                  <a
                    className="btn btn-secondary"
                    data-toggle="modal"
                    onClick={(event: any) =>
                      openAddIntegratorHandler({ Id: -1 }, event)
                    }
                    data-testid="modalbtn"
                    data-target="#addUser"
                  >
                    <svg
                      width="20"
                      height="20"
                      fill="currentColor"
                      className="bi bi-plus m-0"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
                    </svg>
                    <FormattedMessage id="add_integrator" />
                  </a>
                </li>
              )}

              <li title={formatMessage("refresh")}>
                <a
                  data-toggle="modal"
                  className="btn btn-light"
                  onClick={() => callGetIntegratorListService()}
                >
                  <svg
                    width="20"
                    height="20"
                    fill="currentColor"
                    className="bi bi-arrow-repeat m-0"
                    viewBox="0 0 16 16"
                  >
                    <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z" />
                    <path
                      fillRule="evenodd"
                      d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5.002 5.002 0 0 0 8 3zM3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9H3.1z"
                    />
                  </svg>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      {integratorData.length > 0 ? (
        <div className={`${openPanel ? "panelOpen" : ""}`}>
          <div>
            <div className="card changingPanel">
              <div className="tableDesign row-eq-height tableDevices ">
                <div className="table-responsive" data-testid="table">
                  {integratorData && (
                    <Grid
                      data={integratorData}
                      columns={columns}
                      keyField="Id"
                      loading={false}
                      rowClasses={rowClassFormat}
                      rowEvents={{ onClick: rowEvents }}
                    />
                  )}
                </div>
              </div>
            </div>
            {integratorData.length > 0 ? (
              <div className="row no-gutters p-2 tableFooter">
                <div className="col-sm-6 pt-1">
                  <FormattedMessage id="show" />
                  <select
                    data-testid="pageSelect"
                    className="pageLimit"
                    onChange={onPageLimitChanged}
                    value={pageLimitInfo.pageLimit}
                  >
                    {pageLimitOptions.map((op: any) => (
                      <option key={op} value={op}>
                        {op}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-6 d-flex justify-content-sm-end">
                  <Pagination
                    isRefreshPage={pageInfo.isRefreshPage}
                    totalPages={pageInfo.totalPages}
                    pageLimit={pageLimitInfo.pageLimit}
                    onPageChanged={onPageChanged}
                    disablePagination={disablePagination}
                    setIsRefreshPage={setIsRefreshPage}
                  />
                </div>
              </div>
            ) : (
              ""
            )}
          </div>
        </div>
      ) : (
        <div className="message text-center">
          <img src={noMessageIcon} alt="" className="no_message" />
          <p className="noData text-muted text-center h4 mb-0">
            <FormattedMessage id="no_integrator" />
          </p>
          <p className="mb-3">{formatMessage("no_integrator_message")}</p>
          {isOperationPermittedRoleBase(ModuleType.Integrator, "Add") && (
            <LoaderButton
              text="add_integrator"
              onClick={(event: any) =>
                openAddIntegratorHandler({ Id: -1 }, event)
              }
            />
          )}
        </div>
      )}

      <SlidingPanel
        type={"right"}
        isOpen={openPanel}
        noBackdrop={false}
        size={100}
      >
        <div className="bg_slide panel">
          <div className="row bb p-2 pl-4 g-0 align-items-center ">
            <div className="col-lg-10">
              <h2 className="fw-bold mb-0 mt-1 py-2">
                {selectedRow.IntegratorId}
                {/* <FormattedMessage id={"config_details"} /> */}
              </h2>
            </div>
            <div className="col-lg-2">
              <span title={formatMessage("close")}>
                <svg
                  onClick={closePanelhandler}
                  role="button"
                  xlinkTitle="close"
                  xmlns="http://www.w3.org/2000/svg"
                  width="30"
                  height="30"
                  fill="currentColor"
                  className="bi bi-x ms-auto d-block"
                  viewBox="0 0 16 16"
                >
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                </svg>
              </span>
            </div>
          </div>
          <div className="group_detailsWrapper pb-3">
            <IntegratorDetails selectedData={selectedRow} />
          </div>
        </div>
      </SlidingPanel>
    </React.Fragment>
  );
};

export default ConfirmPopupHOC(
  ThresholdIntegratorLinkbaseOutPopupHOC(
    AddIntegratorStatusImagesPopupHOC(
      AddIntegratorPopupHOC(DeletePopupHOC(observer(Integrator)))
    )
  )
);
